import { useCallback, useRef } from 'react';
import { stringPredicate } from 'js/utils/predicate.util';

import { getResponseData, parse, post } from '../helpers';
import { ZIP_REGEX, CITY_REGEX, POSTCODE_PROVIDER_ERROR } from '../constants';
import { useAddressLookupSwissConfig } from './useAddressLookupSwissConfig';

import * as Types from '../types';

export const useSearchZipAndCity = () => {
    const abortController = useRef<AbortController | null>(null);
    const prevSearch = useRef<string>('');
    const sortBy = useRef<string>('');
    const config = useAddressLookupSwissConfig();

    const prepareZipCityRequest = useCallback((searchText: string): Types.IAddressLookupRequest => {
        const zipCode = ZIP_REGEX.test(searchText) ? searchText : '';
        const townName = CITY_REGEX.test(searchText) ? searchText : '';

        sortBy.current = townName && 'city';

        return {
            request: {
                Onrp: 0,
                ZipAddition: '',
                StrId: 0,
                Streetname: '',
                HouseKey: 0,
                HouseNumber: '',
                HouseNumberAddition: '',
                Zipcode: zipCode,
                TownName: townName
            },
            zipOrderMode: 0
        };
    }, []);

    const sort = useCallback((rows: Types.IAutoCompleteItem[]): Types.IAutoCompleteItem[] => {
        if (sortBy.current === 'city') {
            return rows.sort((row1, row2) => {
                const city1 = row1.TownName.toLowerCase();
                const city2 = row2.TownName.toLowerCase();

                if (city1.includes(prevSearch.current) && !city2.includes(prevSearch.current)) {
                    return -1;
                }

                if (!city1.includes(prevSearch.current) && city2.includes(prevSearch.current)) {
                    return 1;
                }

                return stringPredicate(city1, city2);
            });
        }

        return rows.sort((row1, row2) => stringPredicate(row1.Zipcode, row2.Zipcode));
    }, []);

    return useCallback(async (searchText: string): Promise<Types.IParsedAutoCompleteItem[]> => {
        prevSearch.current = searchText;
        abortController.current?.abort();
        abortController.current = new AbortController();

        if (searchText.length < 2 || !ZIP_REGEX.test(searchText) && !CITY_REGEX.test(searchText)) {
            throw {
                errorCode: POSTCODE_PROVIDER_ERROR.NOT_VALID_ERROR,
                errorMessage: 'playtech.mobile.system.form.addressLookupSwiss.internal.zipCityNotValid'
            };
        }

        const params = prepareZipCityRequest(searchText);

        if (params.request.TownName) {
            sortBy.current = 'city';
        }

        try {
            const response = await post(config.url, params, abortController.current?.signal);
            const data = await getResponseData(response);

            sort(data);

            return parse(data);
        } catch (error: any) {
            if (error?.errorCode === POSTCODE_PROVIDER_ERROR.ABORT_ERROR) {
                return [];
            }

            throw error;
        }
    }, [config, prepareZipCityRequest, sort]);
};
